import { createAuthStore } from '@/db/adapters/authStore'
import { createDemoUsers } from '@/db/adapters/demoUsers'
import { assertSessionStatusChangeAllowed } from '@/db/domain/sessionStatus'
import {
  statusRecordsAnEventTime,
  statusRequiresReason,
  statusStartsWork,
} from '@/db/domain/statusRules'
import {
  createInMemoryTanStackDb,
} from '@/db/adapters/tanstackInMemory'
import {
  getAuthorDisplayName as getAuthorDisplayNameQuery,
  getCurrentUser as getCurrentUserQuery,
  loginWithPassword as loginWithPasswordCommand,
  logout as logoutCommand,
  refreshAuth as refreshAuthCommand,
  updatePassword as updatePasswordCommand,
  updateProfile as updateProfileCommand,
} from '@/db/application/auth'
import {
  appendLog as appendLogCommand,
  appendLogWithTransaction as appendLogWithTransactionCommand,
  appendStatusChange as appendStatusChangeCommand,
  createCallout as createCalloutCommand,
  deleteLog as deleteLogCommand,
  discardLog as discardLogCommand,
  updateCallout as updateCalloutCommand,
  updateLog as updateLogCommand,
} from '@/db/application/callouts'
import { appendAddendumWithTransaction as appendAddendumWithTransactionCommand } from '@/db/application/addendums'
import {
  createCustomer as createCustomerCommand,
  deleteCustomer as deleteCustomerCommand,
  updateCustomer as updateCustomerCommand,
} from '@/db/application/customers'
import {
  cancelWhatsAppMessage as cancelWhatsAppMessageCommand,
  holdQueuedWhatsAppMessages as holdQueuedWhatsAppMessagesCommand,
  queueWhatsAppMessage as queueWhatsAppMessageCommand,
  resendWhatsAppMessage as resendWhatsAppMessageCommand,
  whatsappSendDelayMs,
} from '@/db/application/whatsappMessages'
import {
  appendCustomerNote as appendCustomerNoteCommand,
  appendCustomerNoteWithTransaction as appendCustomerNoteWithTransactionCommand,
  deleteCustomerNote as deleteCustomerNoteCommand,
  discardCustomerNote as discardCustomerNoteCommand,
  updateCustomerNote as updateCustomerNoteCommand,
} from '@/db/application/customerNotes'
import {
  addCustomerDocumentWithTransaction as addCustomerDocumentWithTransactionCommand,
  deleteCustomerDocument as deleteCustomerDocumentCommand,
  discardCustomerDocument as discardCustomerDocumentCommand,
} from '@/db/application/customerDocuments'
import {
  assignFollowUp as assignFollowUpCommand,
  closeFollowUp as closeFollowUpCommand,
  completeFollowUp as completeFollowUpCommand,
  createFollowUp as createFollowUpCommand,
  discardFollowUp as discardFollowUpCommand,
  reopenFollowUp as reopenFollowUpCommand,
  rescheduleFollowUp as rescheduleFollowUpCommand,
  snoozeFollowUp as snoozeFollowUpCommand,
  updateFollowUpDetails as updateFollowUpDetailsCommand,
} from '@/db/application/followUps'
import type {
  FollowUp,
  NewFollowUpInput,
  ScheduleStep,
} from '@/db/domain/followUps'
import type { NewJobInitialValues } from '@/screens/NewJobScreen'
import type { CustomerArchivePort } from '@/db/domain/customerArchive'
import {
  validateReminderSettings,
  type ReminderSettingsInput,
} from '@/db/domain/customerReminders'
import type { Customer, Job, JobAssignment, KioskSession, ServiceAssignment } from '@/lib/schemas/db'
import type { KioskSessionFormData, KioskType } from '@/lib/schemas/kiosk'
import { Sentry } from '@/lib/sentry'
import type {
  Addendum,
  AddendumChange,
  AuthResponse,
  DemoUser,
  Log,
  NewAddendumInput,
  NewCalloutInput,
  NewCustomerDocumentInput,
  NewCustomerInput,
  NewCustomerNoteInput,
  NewLogInput,
  NewStatusChangeInput,
  NewWhatsAppMessageInput,
  PhotoLog,
  StatusChangeLog,
  TextLog,
  UpdatePasswordInput,
  UpdateProfileInput,
  UpdateCalloutInput,
  UpdateCustomerInput,
  UpdateCustomerNoteInput,
  UpdateLogInput,
  User,
} from '@/db/domain/models'
import type {
  ArchivedServiceRowsCollectionPort,
  ArchivedServicesSearchPort,
  CustomersSearchPort,
  FollowUpsSearchPort,
  AuthApiPort,
  CalloutsCollectionPort,
  SessionsCollectionPort,
  CustomersCollectionPort,
  NotesCollectionPort,
  SessionNotesCollectionPort,
  JobNotesCollectionPort,
  CustomerNotesCollectionPort,
  CustomerDocumentsCollectionPort,
  FollowUpCategoriesCollectionPort,
  FollowUpsCollectionPort,
  KiosksCollectionPort,
  KioskSessionsCollectionPort,
  DocumentsCollectionPort,
  StatusChangesCollectionPort,
  SessionStatusChangesCollectionPort,
  JobStatusChangesCollectionPort,
  AddendumsCollectionPort,
  SessionAddendumsCollectionPort,
  JobAddendumsCollectionPort,
  CustomerRevisionsCollectionPort,
  ServiceRevisionsCollectionPort,
  JobAssignmentsCollectionPort,
  ServiceAssignmentsCollectionPort,
  ServiceCachePort,
  UserLookupPort,
  UsersCollectionPort,
  JobsCollectionPort,
  JobsRepositoryPort,
  WhatsAppMessagesCollectionPort,
  AppSettingsCollectionPort,
  CustomerRemindersCollectionPort,
  BrandingCollectionPort,
  BrandingApiPort,
  SaveBrandingInput,
  WhatsAppTemplatesCollectionPort,
  WhatsAppTwilioTemplatesCollectionPort,
  SaveWhatsAppTwilioSettingsInput,
  WhatsAppTwilioSettings,
} from '@/db/ports'
import { randomIdGenerator, systemClock } from '@/db/system'

type PocketBaseClientLike = {
  authStore?: {
    record?: Record<string, unknown> | null
    model?: { id?: string | null } | null
  }
  collection: (name: string) => {
    create: <T>(payload: Record<string, unknown>) => Promise<T>
    update: <T>(id: string, payload: Record<string, unknown>) => Promise<T>
  }
}

type KioskCheckoutInput = {
  jobId: string
  checkedOutName?: string
  checkedOutPhone?: string
  signatureFile?: File
  noSignatureReason?: string
  /** Staff user who started the flow (session.startedBy); the submitting device may be a kiosk tablet account. */
  processedBy?: string
}

type CreateKioskJobInput = {
  customerId: string
  formData: KioskSessionFormData
  signatureFile?: File
  noSignatureReason?: string
  /** Staff user who started the flow (session.startedBy); the submitting device may be a kiosk tablet account. */
  processedBy?: string
}

function getCurrentAuthUserId(currentUserId?: string, pbAuthStore?: PocketBaseClientLike['authStore']) {
  if (currentUserId?.trim()) return currentUserId

  const pocketBaseUserId =
    typeof pbAuthStore?.record?.id === 'string'
      ? pbAuthStore.record.id
      : typeof pbAuthStore?.model?.id === 'string'
        ? pbAuthStore.model.id
        : undefined
  return pocketBaseUserId?.trim() ? pocketBaseUserId : undefined
}

function normaliseOptional(value?: string) {
  const trimmed = value?.trim() ?? ''
  return trimmed.length > 0 ? trimmed : undefined
}


function buildKioskJob(
  params: {
    id: string
    now: string
    customerId: string
    formData: KioskSessionFormData
    signatureFile?: File
    noSignatureReason?: string
    checkedInBy?: string
  },
): Job {
  const { formData } = params
  const deviceType = formData.deviceType === 'other' ? formData.deviceTypeOther?.trim() : formData.deviceType?.trim()
  const peripherals = [
    ...(formData.peripherals ?? []).filter((entry) => entry !== 'other'),
    ...(formData.peripherals?.includes('other') && formData.peripheralsOther?.trim()
      ? [formData.peripheralsOther.trim()]
      : []),
  ].join(', ')
  const normalisedNoSignatureReason = normaliseOptional(params.noSignatureReason)

  return {
    id: params.id,
    customerId: params.customerId,
    createdAt: params.now,
    updatedAt: params.now,
    status: 'NewJob',
    contactAddress: normaliseOptional(formData.contactAddress),
    contactName: normaliseOptional(formData.fullName),
    contactPhone: normaliseOptional(formData.contactPhone),
    contactEmail: normaliseOptional(formData.contactEmail),
    invoiceName: normaliseOptional(formData.invoiceName) ?? normaliseOptional(formData.fullName),
    invoiceAddress: normaliseOptional(formData.invoiceAddress) ?? normaliseOptional(formData.contactAddress),
    invoiceVat: normaliseOptional(formData.invoiceVat),
    invoiceEmail: normaliseOptional(formData.invoiceEmail) ?? normaliseOptional(formData.contactEmail),
    invoicePhone: normaliseOptional(formData.invoicePhone) ?? normaliseOptional(formData.contactPhone),
    deviceType: normaliseOptional(deviceType),
    deviceDescription: normaliseOptional(formData.deviceDescription),
    devicePassword: normaliseOptional(formData.devicePassword),
    fault: formData.fault?.trim() ?? '',
    peripherals: peripherals || undefined,
    checkedInBy: normaliseOptional(params.checkedInBy),
    checkedInNoSignatureReason: normalisedNoSignatureReason ?? '',
    checkedInSignedAt: params.now,
    // The signature is uploaded as a File through the adapter's onInsert
    // serializer. The Job model types this field as the stored filename
    // (string); the File only rides here transiently until the refetch below
    // replaces it with the server filename.
    checkedInSignature:
      !normalisedNoSignatureReason && params.signatureFile
        ? (params.signatureFile as unknown as string)
        : undefined,
  }
}

type DbDeps = {
  authStore?: ReturnType<typeof createAuthStore>
  userModule?: UserModule
  authApi?: AuthApiPort
  clock?: typeof systemClock
  idGenerator?: typeof randomIdGenerator
  adapter?: DbAdapter
  pocketBase?: unknown
  adapterFactory?: (ctx: {
    authStore: ReturnType<typeof createAuthStore>
    seedUsers: User[]
  }) => DbAdapter
}

type UserModule = {
  users: DemoUser[]
}

export type DbAdapter = {
  usersCollection: UsersCollectionPort
  customersCollection: CustomersCollectionPort
  calloutsCollection: CalloutsCollectionPort
  sessionsCollection: SessionsCollectionPort
  notesCollection: NotesCollectionPort
  sessionNotesCollection: SessionNotesCollectionPort
  jobNotesCollection: JobNotesCollectionPort
  customerNotesCollection: CustomerNotesCollectionPort
  customerDocumentsCollection: CustomerDocumentsCollectionPort
  followUpsCollection: FollowUpsCollectionPort
  followUpCategoriesCollection: FollowUpCategoriesCollectionPort
  statusChangesCollection: StatusChangesCollectionPort
  sessionStatusChangesCollection: SessionStatusChangesCollectionPort
  jobStatusChangesCollection: JobStatusChangesCollectionPort
  addendumsCollection: AddendumsCollectionPort
  sessionAddendumsCollection: SessionAddendumsCollectionPort
  jobAddendumsCollection: JobAddendumsCollectionPort
  customerRevisionsCollection: CustomerRevisionsCollectionPort
  calloutRevisionsCollection: ServiceRevisionsCollectionPort
  sessionRevisionsCollection: ServiceRevisionsCollectionPort
  jobRevisionsCollection: ServiceRevisionsCollectionPort
  jobAssignmentsCollection: JobAssignmentsCollectionPort
  calloutAssignmentsCollection: ServiceAssignmentsCollectionPort
  sessionAssignmentsCollection: ServiceAssignmentsCollectionPort
  jobsCollection: JobsCollectionPort
  kiosksCollection: KiosksCollectionPort
  kioskSessionsCollection: KioskSessionsCollectionPort
  documentsCollection: DocumentsCollectionPort
  whatsappMessagesCollection: WhatsAppMessagesCollectionPort
  appSettingsCollection: AppSettingsCollectionPort
  customerRemindersCollection: CustomerRemindersCollectionPort
  /** Block every message to this reminder's customer until resumed. */
  stopCustomerReminder: (id: string) => Promise<void>
  resumeCustomerReminder: (id: string) => Promise<void>
  brandingCollection: BrandingCollectionPort
  saveBranding: BrandingApiPort['saveBranding']
  whatsappTemplatesCollection: WhatsAppTemplatesCollectionPort
  whatsappTwilioTemplatesCollection: WhatsAppTwilioTemplatesCollectionPort
  /** Pull the latest Content templates from Twilio into the cache collection. */
  requestTwilioTemplateSync: () => Promise<{ ok: boolean; result?: string }>
  /** Admin-only Twilio connection settings (server routes; token stays server-side). */
  loadWhatsAppTwilioSettings: () => Promise<WhatsAppTwilioSettings>
  saveWhatsAppTwilioSettings: (
    input: SaveWhatsAppTwilioSettingsInput,
  ) => Promise<WhatsAppTwilioSettings>
  /** Fetch a job straight from the server (fresh server-assigned fields). */
  getFreshJob?: (jobId: string) => Promise<Job | undefined>
  kioskPairedDevicesCollection?: { refetch?: () => Promise<unknown> } & any
  usersLookup: UserLookupPort
  archivedServicesCollection: ArchivedServiceRowsCollectionPort
  searchArchivedServices: ArchivedServicesSearchPort['searchArchivedServices']
  loadServiceNotes: ServiceCachePort['loadServiceNotes']
  searchCustomers: CustomersSearchPort['searchCustomers']
  /** Archive / restore / relink — see db/domain/customerArchive.ts. */
  previewCustomerArchive: CustomerArchivePort['previewCustomerArchive']
  archiveCustomer: CustomerArchivePort['archiveCustomer']
  unarchiveCustomer: CustomerArchivePort['unarchiveCustomer']
  relinkServiceCustomer: CustomerArchivePort['relinkServiceCustomer']
  searchFollowUps: FollowUpsSearchPort['searchFollowUps']
  /**
   * Loads one customer's follow-ups (all statuses and due dates) into the
   * collection — the customer page needs the slices the due-only sync skips.
   */
  loadCustomerFollowUps: (customerId: string) => Promise<void>
  /** Short-lived token for reading protected files (customer documents). */
  getFileToken: () => Promise<string>
  // The record-cache surface — see ServiceCachePort for the contract. In the
  // in-memory adapter hydrateServiceDetails resolves false and hydrateRecord
  // true: everything is already local there.
  hydrateServiceDetails: ServiceCachePort['hydrateServiceDetails']
  hydrateRecord: ServiceCachePort['hydrateRecord']
  /**
   * Updates a customer straight through the backend, bypassing the local
   * collection. Kiosk tablets never initialize the customers collection
   * sync, so collection writes throw SyncNotInitializedError there; this
   * path only mirrors the result into the collection when its sync is
   * already running (e.g. the receptionist panel).
   */
  updateCustomerDirect: (
    id: string,
    patch: UpdateCustomerInput,
  ) => Promise<Customer>
  hydrateUsers?: () => Promise<void>
  /**
   * Opens the server realtime stream: every collection subscribed, events
   * used as refetch signals for whatever is in view (ADR 0002). Idempotent
   * while attached; only meaningful once authenticated.
   */
  attachRealtime?: () => Promise<void>
  /** Closes the realtime stream; call on logout or token change. */
  detachRealtime?: () => void
}

/**
 * Composes the data layer for a given environment.
 * Swap the injected ports/adapters to target PocketBase, fakes, or other caches.
 */
export function createDb(deps: DbDeps = {}) {
  const clock = deps.clock ?? systemClock
  const idGenerator = deps.idGenerator ?? randomIdGenerator
  const authStore = deps.authStore ?? createAuthStore()
  const userModule: UserModule = deps.userModule ?? {
    users: createDemoUsers(clock),
  }
  const userDirectory = userModule.users.map(({ password, ...user }) => user)

  const authApi: AuthApiPort =
    deps.authApi ??
    ({
      async loginWithPassword(identity: string, password: string) {
        await new Promise((resolve) => setTimeout(resolve, 150))

        const match = userModule.users.find(
          (user) => user.email.toLowerCase() === identity.toLowerCase(),
        )

        if (!match || match.password !== password) {
          throw new Error('Invalid email or password')
        }

        const token = `demo_${idGenerator.generateId()}`
        const user: User = {
          id: match.id,
          createdAt: match.createdAt,
          updatedAt: clock.now(),
          name: match.name,
          email: match.email,
          avatarUrl: match.avatarUrl,
        }

        return { token, user }
      },
      async refreshAuth(_deviceUuid?: string) {
        const token = authStore.token
        const user = authStore.user
        if (!token || !user) {
          throw new Error('No authenticated user available for refresh')
        }
        return { token, user }
      },
      async updateProfile(userId: string, input: UpdateProfileInput) {
        await new Promise((resolve) => setTimeout(resolve, 150))

        const index = userModule.users.findIndex(
          (user) => user.id === userId,
        )
        if (index < 0) {
          throw new Error('User not found for profile update')
        }

        const existing = userModule.users[index]
        const nextAvatarUrl =
          input.removeAvatar === true
            ? undefined
            : input.avatarUrl ?? existing.avatarUrl

        const updated: DemoUser = {
          ...existing,
          name: input.name,
          email: input.email,
          avatarUrl: nextAvatarUrl,
          updatedAt: clock.now(),
        }
        userModule.users[index] = updated

        const { password, ...user } = updated
        return user
      },
      async updatePassword(userId: string, input: UpdatePasswordInput) {
        await new Promise((resolve) => setTimeout(resolve, 150))

        const index = userModule.users.findIndex(
          (user) => user.id === userId,
        )
        if (index < 0) {
          throw new Error('User not found for password update')
        }

        const existing = userModule.users[index]
        if (existing.password !== input.currentPassword) {
          throw new Error('Current password is incorrect')
        }
        if (input.newPassword !== input.confirmPassword) {
          throw new Error('New password confirmation does not match')
        }

        userModule.users[index] = {
          ...existing,
          password: input.newPassword,
          updatedAt: clock.now(),
        }
      },
    } satisfies AuthApiPort)

  const adapter =
    deps.adapter ??
    deps.adapterFactory?.({ authStore, seedUsers: userDirectory }) ??
    createInMemoryTanStackDb(userDirectory, { authStore })

  const hydrateUsersRaw =
    'hydrateUsers' in adapter ? adapter.hydrateUsers : undefined
  // Adapters without hydrateUsers (in-memory) seed their directory
  // synchronously, so they start hydrated.
  let usersHydrated = !hydrateUsersRaw
  const hydrateUsers = hydrateUsersRaw
    ? async () => {
        await hydrateUsersRaw()
        usersHydrated = true
      }
    : undefined
  const attachRealtime =
    'attachRealtime' in adapter ? adapter.attachRealtime : undefined
  const detachRealtime =
    'detachRealtime' in adapter ? adapter.detachRealtime : undefined
  const createCustomerMutation = (input: NewCustomerInput) =>
    createCustomerCommand(input, {
      clock,
      idGenerator,
      customersRepo: adapter.customersCollection,
    })
  const updateCustomerMutation = (
    id: string,
    patch: UpdateCustomerInput,
  ) =>
    updateCustomerCommand(id, patch, {
      authStore,
      clock,
      idGenerator,
      customersRepo: adapter.customersCollection,
      customerRevisionsRepo: adapter.customerRevisionsCollection,
    })
  const createCalloutMutation = (input: NewCalloutInput) => {
    return createCalloutCommand(input, {
      authStore,
      clock,
      idGenerator,
      calloutsRepo: adapter.calloutsCollection,
    })
  }
  const createSessionMutation = (input: NewCalloutInput) => {
    return createCalloutCommand(input, {
      authStore,
      clock,
      idGenerator,
      calloutsRepo: adapter.sessionsCollection,
    })
  }
  const updateCalloutMutation = (
    id: string,
    patch: UpdateCalloutInput,
  ) => {
    const nextStatus = patch.status
    const current = adapter.calloutsCollection.get?.(id)

    if (
      current &&
      nextStatus &&
      (current.status !== nextStatus || Boolean(patch.statusReason?.trim()))
    ) {
      try {
        appendStatusChangeCommand(id, {
          previousStatus: current.status,
          newStatus: nextStatus,
          reason: patch.statusReason,
          occurredAt: patch.statusOccurredAt,
        }, {
          authStore,
          clock,
          idGenerator,
          calloutsRepo: adapter.calloutsCollection,
          statusChangesRepo: adapter.statusChangesCollection,
        })
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error)
        if (message.includes('Status change stale write')) {
          const latest = adapter.calloutsCollection.get?.(id)
          if (!latest || latest.status === nextStatus) {
            return updateCalloutCommand(id, patch, {
              clock,
              calloutsRepo: adapter.calloutsCollection,
            })
          }
        }
        throw error
      }
    }

    return updateCalloutCommand(id, patch, {
      clock,
      calloutsRepo: adapter.calloutsCollection,
    })
  }

  // Same command path as updateCalloutMutation, pointed at the sessions
  // collections: appends the status-change log and validates transitions
  // instead of the ad-hoc collection writes sessions used before.
  const updateSessionMutation = (
    id: string,
    patch: UpdateCalloutInput,
  ) => {
    const nextStatus = patch.status
    const current = adapter.sessionsCollection.get?.(id)

    if (
      current &&
      nextStatus &&
      (current.status !== nextStatus || Boolean(patch.statusReason?.trim()))
    ) {
      try {
        appendStatusChangeCommand(id, {
          previousStatus: current.status,
          newStatus: nextStatus,
          reason: patch.statusReason,
          occurredAt: patch.statusOccurredAt,
        }, {
          authStore,
          clock,
          idGenerator,
          calloutsRepo: adapter.sessionsCollection,
          statusChangesRepo: adapter.sessionStatusChangesCollection,
        })
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error)
        if (message.includes('Status change stale write')) {
          const latest = adapter.sessionsCollection.get?.(id)
          if (!latest || latest.status === nextStatus) {
            return updateCalloutCommand(id, patch, {
              clock,
              calloutsRepo: adapter.sessionsCollection,
              assertStatusChangeAllowed: assertSessionStatusChangeAllowed,
            })
          }
        }
        throw error
      }
    }

    return updateCalloutCommand(id, patch, {
      clock,
      calloutsRepo: adapter.sessionsCollection,
      assertStatusChangeAllowed: assertSessionStatusChangeAllowed,
    })
  }

  // Retires the latest active assignment (if it's someone else) and creates or
  // revives the assignment for the requested user. Shared by all service types.
  function assignUserToService<
    T extends {
      id: string
      userId: string
      assignedAt?: string
      unassignedAt?: string
    },
  >(opts: {
    collection: {
      values: () => Iterable<unknown>
      update: (id: string, updater: (draft: T) => void) => { isPersisted: { promise: Promise<unknown> } }
      insert: (record: T) => { isPersisted: { promise: Promise<unknown> } }
    }
    isForService: (assignment: T) => boolean
    buildAssignment: (params: { id: string; authorId: string; now: string }) => T
    userId: string
  }) {
    const currentUser = authStore.user
    if (!currentUser) {
      throw new Error('Not authenticated')
    }

    const activeForService = (Array.from(opts.collection.values()) as T[]).filter(
      (assignment) => opts.isForService(assignment) && !assignment.unassignedAt,
    )
    const now = clock.now()

    const unassignTransactions = activeForService
      .filter((assignment) => assignment.userId !== opts.userId)
      .map((assignment) =>
        opts.collection.update(assignment.id, (draft) => {
          draft.unassignedAt = now
          ;(draft as { updatedAt?: string }).updatedAt = now
        }),
      )

    const existingAssignment = activeForService.find(
      (assignment) => assignment.userId === opts.userId,
    )
    const assignmentTransaction = existingAssignment
      ? opts.collection.update(existingAssignment.id, (draft) => {
          draft.unassignedAt = undefined
          ;(draft as { updatedAt?: string }).updatedAt = now
        })
      : opts.collection.insert(
          opts.buildAssignment({
            id: idGenerator.generateId(),
            authorId: currentUser.id,
            now,
          }),
        )

    return { assignmentTransaction, unassignTransactions }
  }

  const assignServiceAssignment = (
    collection: DbAdapter['calloutAssignmentsCollection'],
    serviceId: string,
    userId: string,
  ) =>
    assignUserToService<ServiceAssignment>({
      collection,
      isForService: (assignment) => assignment.serviceId === serviceId,
      buildAssignment: ({ id, authorId, now }) => ({
        id,
        serviceId,
        userId,
        authorId,
        assignedAt: now,
        unassignedAt: undefined,
        createdAt: now,
        updatedAt: now,
      }),
      userId,
    })

  const awaitAssignResult = async (result: {
    assignmentTransaction: { isPersisted: { promise: Promise<unknown> } }
    unassignTransactions: Array<{ isPersisted: { promise: Promise<unknown> } } | undefined>
  }) => {
    await Promise.all(
      result.unassignTransactions.map((transaction) => transaction?.isPersisted.promise),
    )
    await result.assignmentTransaction.isPersisted.promise
  }

  // When someone moves a service into a work-started status (job InProgress,
  // session Started), claim it for them — but only if no one holds it yet.
  // An existing assignment (to anyone) is left alone; this fills the gap, it
  // doesn't reassign. Best-effort: a failure here must not fail the status
  // change that triggered it.
  const autoAssignOnWorkStartIfUnassigned = async (opts: {
    nextStatus?: string
    collection: {
      values: () => Iterable<unknown>
      preload?: () => Promise<unknown>
    }
    isForService: (assignment: { unassignedAt?: string }) => boolean
    assign: (userId: string) => Parameters<typeof awaitAssignResult>[0]
  }) => {
    if (!opts.nextStatus || !statusStartsWork(opts.nextStatus)) return
    const userId = authStore.user?.id
    if (!userId) return
    try {
      await opts.collection.preload?.()
      const alreadyAssigned = (
        Array.from(opts.collection.values()) as Array<{ unassignedAt?: string }>
      ).some((assignment) => opts.isForService(assignment) && !assignment.unassignedAt)
      if (alreadyAssigned) return
      await awaitAssignResult(opts.assign(userId))
    } catch (error) {
      Sentry.captureException(error, {
        tags: { module: 'auto-assign' },
        extra: { nextStatus: opts.nextStatus },
      })
    }
  }

  // Reads the admin-configured send delay; falls back to the 30s default when
  // settings haven't loaded (or don't exist yet).
  const currentWhatsAppSendDelayMs = async () => {
    try {
      await adapter.appSettingsCollection.preload()
    } catch {
      // fall through to the default below
    }
    const settings = Array.from(adapter.appSettingsCollection.values())[0]
    return whatsappSendDelayMs(settings)
  }

  // Whether admins have switched WhatsApp dispatch on. Defaults off until a
  // settings row says otherwise. The server-side sender is the real enforcer;
  // this lets the app reflect the state.
  const isWhatsAppSendingEnabled = async () => {
    try {
      await adapter.appSettingsCollection.preload()
    } catch {
      return false
    }
    const settings = Array.from(adapter.appSettingsCollection.values())[0]
    return settings?.whatsappSendingEnabled ?? false
  }

  // Admin curation of the follow-up category list (Settings page). The server
  // enforces the gate: follow_up_categories write rules require is_admin.
  const sortedFollowUpCategories = () =>
    Array.from(adapter.followUpCategoriesCollection.values()).sort(
      (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
    )

  const createFollowUpCategory = async (name: string): Promise<void> => {
    const trimmed = name.trim()
    if (!trimmed) throw new Error('A category name is required')
    await adapter.followUpCategoriesCollection.preload?.()
    const existing = sortedFollowUpCategories()
    if (existing.some((c) => c.name.toLowerCase() === trimmed.toLowerCase())) {
      throw new Error('That category already exists')
    }
    const maxOrder = existing.reduce((max, c) => Math.max(max, c.sortOrder), 0)
    const transaction = adapter.followUpCategoriesCollection.insert({
      id: idGenerator.generateId(),
      name: trimmed,
      sortOrder: maxOrder + 1,
    })
    await transaction.isPersisted.promise
  }

  const renameFollowUpCategory = async (id: string, name: string): Promise<void> => {
    const trimmed = name.trim()
    if (!trimmed) throw new Error('A category name is required')
    await adapter.followUpCategoriesCollection.preload?.()
    const existing = sortedFollowUpCategories()
    if (
      existing.some(
        (c) => c.id !== id && c.name.toLowerCase() === trimmed.toLowerCase(),
      )
    ) {
      throw new Error('That category already exists')
    }
    const transaction = adapter.followUpCategoriesCollection.update(id, (draft) => {
      draft.name = trimmed
    })
    await transaction.isPersisted.promise
  }

  // Deleting never touches follow-ups (no cascade); a dangling reference just
  // reads as uncategorised.
  const deleteFollowUpCategory = async (id: string): Promise<void> => {
    const transaction = adapter.followUpCategoriesCollection.delete(id)
    await transaction.isPersisted.promise
  }

  // Persist a full drag-and-drop ordering: sequential sort_order following
  // orderedIds (also heals any duplicate/zero sort_order left over from hand
  // edits in the dashboard). Ids not listed keep their relative order after
  // the listed ones.
  const reorderFollowUpCategories = async (orderedIds: string[]): Promise<void> => {
    await adapter.followUpCategoriesCollection.preload?.()
    const list = sortedFollowUpCategories()
    const rank = new Map(orderedIds.map((id, index) => [id, index]))
    const reordered = [...list].sort((a, b) => {
      const ra = rank.get(a.id)
      const rb = rank.get(b.id)
      if (ra !== undefined && rb !== undefined) return ra - rb
      if (ra !== undefined) return -1
      if (rb !== undefined) return 1
      return 0
    })
    for (let i = 0; i < reordered.length; i += 1) {
      const category = reordered[i]
      const wanted = i + 1
      if (category.sortOrder === wanted) continue
      const transaction = adapter.followUpCategoriesCollection.update(
        category.id,
        (draft) => {
          draft.sortOrder = wanted
        },
      )
      await transaction.isPersisted.promise
    }
  }

  // Change the queued-message cancel window by updating the app_settings
  // singleton through the collection (adapter is the PocketBase gateway).
  const setWhatsAppSendDelay = async (seconds: number): Promise<void> => {
    if (!Number.isInteger(seconds) || seconds < 0) {
      throw new Error('Send delay must be a whole number of seconds (0 or more)')
    }
    await adapter.appSettingsCollection.preload?.()
    const settings = Array.from(adapter.appSettingsCollection.values())[0]
    if (!settings) throw new Error('App settings not found')
    const transaction = adapter.appSettingsCollection.update(
      settings.id,
      (draft) => {
        draft.whatsappSendDelaySeconds = seconds
      },
    )
    await transaction.isPersisted.promise
  }

  // Admin edit of the reminder schedule, max age and send window (the
  // app_settings singleton is staff-writable; the Settings page is admin-only).
  const saveReminderSettings = async (input: ReminderSettingsInput): Promise<void> => {
    const problem = validateReminderSettings(input)
    if (problem) throw new Error(problem)
    await adapter.appSettingsCollection.preload?.()
    const settings = Array.from(adapter.appSettingsCollection.values())[0]
    if (!settings) throw new Error('App settings not found')
    const transaction = adapter.appSettingsCollection.update(
      settings.id,
      (draft) => {
        draft.reminderScheduleSeconds = input.scheduleSeconds
        draft.reminderRepeatSeconds = input.repeatSeconds
        draft.reminderMaxAgeDays = input.maxAgeDays
      },
    )
    await transaction.isPersisted.promise
  }

  // Flip the master sending switch by updating the app_settings singleton
  // through the collection (adapter is the PocketBase gateway) — no direct PB
  // calls from the UI/command layer.
  const setWhatsAppSending = async (enabled: boolean): Promise<boolean> => {
    await adapter.appSettingsCollection.preload?.()
    const settings = Array.from(adapter.appSettingsCollection.values())[0]
    if (!settings) throw new Error('App settings not found')
    const transaction = adapter.appSettingsCollection.update(
      settings.id,
      (draft) => {
        draft.whatsappSendingEnabled = enabled
      },
    )
    await transaction.isPersisted.promise

    // Park anything still queued so re-enabling later won't auto-flush messages
    // that were sitting in the queue while sending was off (the server enforces
    // this too; this keeps the open UI in step immediately).
    if (!enabled) {
      await adapter.whatsappMessagesCollection.preload?.()
      holdQueuedWhatsAppMessagesCommand(
        Array.from(adapter.whatsappMessagesCollection.values()),
        { clock, whatsappMessagesRepo: adapter.whatsappMessagesCollection },
      )
    }
    return enabled
  }

  // Save a purpose's template config (content SID + variable→field mapping)
  // through the collection gateway.
  const saveWhatsAppTemplateConfig = async (
    id: string,
    contentSid: string | undefined,
    variables: Record<string, string>,
  ): Promise<void> => {
    const transaction = adapter.whatsappTemplatesCollection.update(
      id,
      (draft) => {
        draft.contentSid = contentSid || undefined
        draft.variables = variables
      },
    )
    await transaction.isPersisted.promise
  }

  const queueWhatsAppMessageMutation = async (input: NewWhatsAppMessageInput) =>
    queueWhatsAppMessageCommand(
      { authorId: authStore.user?.id, ...input },
      {
        clock,
        idGenerator,
        whatsappMessagesRepo: adapter.whatsappMessagesCollection,
        graceMs: await currentWhatsAppSendDelayMs(),
        // Composed while sending is off → queue as Held (won't auto-send when
        // sending is re-enabled; needs a manual resend).
        sendingEnabled: await isWhatsAppSendingEnabled(),
      },
    )
  const cancelWhatsAppMessageMutation = (id: string) =>
    cancelWhatsAppMessageCommand(id, {
      clock,
      whatsappMessagesRepo: adapter.whatsappMessagesCollection,
    })
  const resendWhatsAppMessageMutation = async (id: string) =>
    resendWhatsAppMessageCommand(id, {
      clock,
      whatsappMessagesRepo: adapter.whatsappMessagesCollection,
      graceMs: await currentWhatsAppSendDelayMs(),
    })

  const unknownAuthorWarnings = new Set<string>()

  // Every follow-up transition can write an outcome note into the customer's
  // thread, so they all take the same set — worth naming once.
  const followUpDeps = {
    authStore,
    clock,
    idGenerator,
    followUpsRepo: adapter.followUpsCollection,
    customerNotesRepo: adapter.customerNotesCollection,
  }

  const db = {
    authStore,
    pb: deps.pocketBase,
    usersCollection: adapter.usersCollection,
    customersCollection: adapter.customersCollection,
    calloutsCollection: adapter.calloutsCollection,
    sessionsCollection: adapter.sessionsCollection,
    notesCollection: adapter.notesCollection,
    sessionNotesCollection: adapter.sessionNotesCollection,
    jobNotesCollection: adapter.jobNotesCollection,
    customerNotesCollection: adapter.customerNotesCollection,
    customerDocumentsCollection: adapter.customerDocumentsCollection,
    followUpsCollection: adapter.followUpsCollection,
    followUpCategoriesCollection: adapter.followUpCategoriesCollection,
    /** Admin-only category curation (server-enforced via users.is_admin). */
    createFollowUpCategory,
    renameFollowUpCategory,
    deleteFollowUpCategory,
    reorderFollowUpCategories,
    statusChangesCollection: adapter.statusChangesCollection,
    sessionStatusChangesCollection: adapter.sessionStatusChangesCollection,
    jobStatusChangesCollection: adapter.jobStatusChangesCollection,
    addendumsCollection: adapter.addendumsCollection,
    sessionAddendumsCollection: adapter.sessionAddendumsCollection,
    jobAddendumsCollection: adapter.jobAddendumsCollection,
    customerRevisionsCollection: adapter.customerRevisionsCollection,
    calloutRevisionsCollection: adapter.calloutRevisionsCollection,
    sessionRevisionsCollection: adapter.sessionRevisionsCollection,
    jobRevisionsCollection: adapter.jobRevisionsCollection,
    jobAssignmentsCollection: adapter.jobAssignmentsCollection,
    calloutAssignmentsCollection: adapter.calloutAssignmentsCollection,
    sessionAssignmentsCollection: adapter.sessionAssignmentsCollection,
    jobsCollection: adapter.jobsCollection,
    kiosksCollection: adapter.kiosksCollection,
    kioskSessionsCollection: adapter.kioskSessionsCollection,
    documentsCollection: adapter.documentsCollection,
    whatsappMessagesCollection: adapter.whatsappMessagesCollection,
    appSettingsCollection: adapter.appSettingsCollection,
    customerRemindersCollection: adapter.customerRemindersCollection,
    stopCustomerReminder: adapter.stopCustomerReminder,
    resumeCustomerReminder: adapter.resumeCustomerReminder,
    saveReminderSettings,
    brandingCollection: adapter.brandingCollection,
    /** Admin-only edit of the shop name/logos (server-enforced via users.is_admin). */
    saveBranding: (input: SaveBrandingInput) => adapter.saveBranding(input),
    whatsappTemplatesCollection: adapter.whatsappTemplatesCollection,
    whatsappTwilioTemplatesCollection: adapter.whatsappTwilioTemplatesCollection,
    isWhatsAppSendingEnabled,
    setWhatsAppSending,
    saveWhatsAppTemplateConfig,
    requestTwilioTemplateSync: () => adapter.requestTwilioTemplateSync(),
    /** Admin-only Twilio connection settings (server-enforced via users.is_admin). */
    loadWhatsAppTwilioSettings: () => adapter.loadWhatsAppTwilioSettings(),
    saveWhatsAppTwilioSettings: (input: SaveWhatsAppTwilioSettingsInput) =>
      adapter.saveWhatsAppTwilioSettings(input),
    setWhatsAppSendDelay,
    queueWhatsAppMessage: queueWhatsAppMessageMutation,
    cancelWhatsAppMessage: cancelWhatsAppMessageMutation,
    resendWhatsAppMessage: resendWhatsAppMessageMutation,
    kioskPairedDevicesCollection: 'kioskPairedDevicesCollection' in adapter ? (adapter as { kioskPairedDevicesCollection?: typeof adapter }).kioskPairedDevicesCollection : undefined,
    archivedServicesCollection: adapter.archivedServicesCollection,
    searchArchivedServices: adapter.searchArchivedServices,
    searchCustomers: adapter.searchCustomers,
    previewCustomerArchive: adapter.previewCustomerArchive,
    archiveCustomer: adapter.archiveCustomer,
    unarchiveCustomer: adapter.unarchiveCustomer,
    relinkServiceCustomer: adapter.relinkServiceCustomer,
    searchFollowUps: adapter.searchFollowUps,
    loadCustomerFollowUps: adapter.loadCustomerFollowUps,
    getFileToken: adapter.getFileToken,
    loadServiceNotes: adapter.loadServiceNotes,
    hydrateServiceDetails: adapter.hydrateServiceDetails,
    hydrateUsers,
    attachRealtime,
    detachRealtime,
    getAuthorDisplayName(authorId: string) {
      const displayName = getAuthorDisplayNameQuery(authorId, {
        authStore,
        userLookup: adapter.usersLookup,
        userDirectory,
      })

      // Screens can map rows while usersCollection.preload() is still in
      // flight; until it lands, a miss says nothing about the id being bad.
      if (
        displayName === 'Unknown user' &&
        usersHydrated &&
        !unknownAuthorWarnings.has(authorId)
      ) {
        unknownAuthorWarnings.add(authorId)
        const message = `Unknown user encountered in status history: ${authorId}`
        console.warn(message)
        Sentry.captureMessage(message, { level: 'warning' })
      }

      return displayName
    },
    async loginWithPassword(identity: string, password: string) {
      const result = await loginWithPasswordCommand(identity, password, {
        authStore,
        authApi,
      })
      try {
        await hydrateUsers?.()
      } catch (error) {
        console.warn('Post-login user hydration failed', error)
      }
      return result
    },
    async logout() {
      await logoutCommand(authStore)
    },
    async refreshAuth(deviceUuid?: string) {
      const result = await refreshAuthCommand({
        authStore,
        authApi,
        deviceUuid,
      })
      try {
        await hydrateUsers?.()
      } catch (error) {
        console.warn('Post-refresh user hydration failed', error)
      }
      return result
    },
    async updateProfile(input: UpdateProfileInput) {
      return updateProfileCommand(input, {
        authStore,
        authApi,
      })
    },
    async updatePassword(input: UpdatePasswordInput) {
      await updatePasswordCommand(input, {
        authStore,
        authApi,
      })
    },
    getCurrentUser() {
      return getCurrentUserQuery(authStore)
    },
    createCustomer(input: NewCustomerInput) {
      const { customer } = createCustomerMutation(input)
      return customer
    },
    async createCustomerAndWait(input: NewCustomerInput) {
      const { customer, transaction } = createCustomerMutation(input)
      await transaction.isPersisted.promise
      return customer
    },
    updateCustomer(id: string, patch: UpdateCustomerInput) {
      updateCustomerMutation(id, patch)
    },
    async updateCustomerAndWait(id: string, patch: UpdateCustomerInput) {
      // The record may not be downloaded yet (collections load lazily) — and
      // updateCustomer silently no-ops on records the collection doesn't have.
      await adapter.hydrateRecord('customer', id)
      const transaction = updateCustomerMutation(id, patch)
      if (!transaction) return
      await transaction.isPersisted.promise
    },
    async upsertKioskCustomerAndWait(id: string, patch: UpdateCustomerInput) {
      // Kiosk customer edits happen on the tablet (and mid-wizard), which
      // never loads the customers collection — its sync is uninitialized
      // there, so any collection-mediated write throws. Go straight through
      // the adapter instead; it still owns the PocketBase write.
      return adapter.updateCustomerDirect(id, patch)
    },
    deleteCustomer(id: string) {
      return deleteCustomerCommand(id, {
        customersRepo: adapter.customersCollection,
      })
    },
    async deleteCustomerAndWait(id: string) {
      // See updateCustomerAndWait: load the record first or the delete
      // silently no-ops.
      await adapter.hydrateRecord('customer', id)
      const transaction = deleteCustomerCommand(id, {
        customersRepo: adapter.customersCollection,
      })
      if (!transaction) return
      await transaction.isPersisted.promise
    },
    createCallout(input: NewCalloutInput) {
      const { callout } = createCalloutMutation(input)
      return callout
    },
    async createCalloutAndWait(input: NewCalloutInput) {
      const { callout, transaction } = createCalloutMutation(input)
      await transaction.isPersisted.promise

      appendStatusChangeCommand(callout.id, {
        previousStatus: 'New',
        newStatus: callout.status,
      }, {
        authStore,
        clock,
        idGenerator,
        calloutsRepo: adapter.calloutsCollection,
        statusChangesRepo: adapter.statusChangesCollection,
      })

      return callout
    },
    createSession(input: NewCalloutInput) {
      const { callout } = createSessionMutation(input)
      return callout
    },
    async createSessionAndWait(input: NewCalloutInput) {
      const { callout, transaction } = createSessionMutation(input)
      await transaction.isPersisted.promise

      appendStatusChangeCommand(callout.id, {
        previousStatus: 'New',
        newStatus: callout.status,
      }, {
        authStore,
        clock,
        idGenerator,
        calloutsRepo: adapter.sessionsCollection,
        statusChangesRepo: adapter.sessionStatusChangesCollection,
      })

      return callout
    },
    createJob(input: NewJobInitialValues) {
      const now = clock.now()
      const currentUser = authStore.user
      if (!currentUser) {
        throw new Error('Not authenticated')
      }
      if (!input.customerId || input.customerId.trim().length === 0) {
        throw new Error('Customer is required for a Job.')
      }
      const customer = adapter.customersCollection.get?.(input.customerId) as Customer | undefined
      const job = {
        id: idGenerator.generateId(),
        customerId: input.customerId,
        authorId: currentUser.id,
        status: 'NewJob' as const,
        contactAddress: customer?.contactAddress,
        contactName: customer?.fullName || input.customerName,
        contactPhone: customer?.contactPhone,
        contactEmail: customer?.contactEmail,
        invoiceName: customer?.invoiceName || customer?.fullName || input.customerName,
        invoiceAddress: customer?.invoiceAddress,
        invoiceVat: customer?.invoiceVat,
        invoiceEmail: customer?.invoiceEmail,
        invoicePhone: customer?.invoicePhone,
        deviceType: input.deviceType,
        deviceDescription: input.deviceDescription,
        devicePassword: input.devicePassword,
        fault: input.fault,
        peripherals: input.peripherals,
        createdAt: now,
        updatedAt: now,
      }
      const transaction = adapter.jobsCollection.insert(job as any)
      return { job, transaction }
    },
    async createJobAndWait(input: NewJobInitialValues) {
      const { job, transaction } = this.createJob(input)
      await transaction.isPersisted.promise

      const persistedJob = adapter.jobsCollection.get?.(job.id) ?? job

      const statusChange: StatusChangeLog = {
        id: idGenerator.generateId(),
        serviceId: persistedJob.id,
        authorId: authStore.user?.id ?? persistedJob.authorId ?? '',
        previousStatus: 'NewJob',
        newStatus: persistedJob.status,
        createdAt: clock.now(),
      }
      adapter.jobStatusChangesCollection.insert(statusChange)

      return persistedJob
    },
    updateKioskSession(id: string, patch: Partial<KioskSession>) {
      const current = adapter.kioskSessionsCollection.get?.(id)
      if (!current) return

      return adapter.kioskSessionsCollection.update(id, (draft) => {
        Object.assign(draft, patch)
        draft.updatedAt = clock.now()
      })
    },
    async updateKioskSessionAndWait(id: string, patch: Partial<KioskSession>) {
      const transaction = this.updateKioskSession(id, patch)
      if (!transaction) return
      await transaction.isPersisted.promise
    },
    getKioskSession(id: string) {
      return adapter.kioskSessionsCollection.get?.(id)
    },
    updateKioskSessionWithLatest(id: string, updater: (current: KioskSession) => Partial<KioskSession>) {
      const current = adapter.kioskSessionsCollection.get?.(id)
      if (!current) return

      return adapter.kioskSessionsCollection.update(id, (draft) => {
        const patch = updater(structuredClone(current))
        Object.assign(draft, patch)
        draft.updatedAt = clock.now()
      })
    },
    async updateKioskSessionWithLatestAndWait(id: string, updater: (current: KioskSession) => Partial<KioskSession>) {
      const transaction = this.updateKioskSessionWithLatest(id, updater)
      if (!transaction) return
      await transaction.isPersisted.promise
    },
    resetKioskSession(id: string) {
      const current = adapter.kioskSessionsCollection.get?.(id)
      if (!current) return
      return adapter.kioskSessionsCollection.update(id, (draft) => {
        draft.flow = 'idle'
        draft.currentStep = 1
        draft.formData = {}
        draft.jobId = undefined
        draft.lastCompletedFlow = undefined
        draft.startedBy = undefined
        draft.updatedAt = clock.now()
      })
    },
    async resetKioskSessionAndWait(id: string) {
      const transaction = this.resetKioskSession(id)
      if (!transaction) return
      await transaction.isPersisted.promise
    },
    async ensureKioskSessionExistsAndWait(kioskId: string, kioskType: KioskType = 'check-in') {
      await adapter.kioskSessionsCollection.preload?.()

      const existing = adapter.kioskSessionsCollection.toArray.find(
        (session) => session.kioskId === kioskId,
      ) as KioskSession | undefined
      if (existing) return existing

      const now = clock.now()
      const session: KioskSession = {
        id: idGenerator.generateId(),
        createdAt: now,
        updatedAt: now,
        kioskId,
        kioskType,
        flow: 'idle',
        currentStep: 1,
        formData: {},
      }

      const transaction = adapter.kioskSessionsCollection.insert(session)
      await transaction.isPersisted.promise
      return adapter.kioskSessionsCollection.get?.(session.id) ?? session
    },
    updateJob(
      id: string,
      patch: Partial<Job> & { statusReason?: string; statusOccurredAt?: string },
    ) {
      const current = adapter.jobsCollection.get?.(id)
      if (!current) return

      const nextStatus = patch.status
      const statusReason = patch.statusReason
      // Jobs write their status change inline (no command), so apply the same
      // rule here: only event-marking statuses carry an occurred-at.
      const statusOccurredAt =
        nextStatus && statusRecordsAnEventTime(nextStatus)
          ? patch.statusOccurredAt?.trim() || undefined
          : undefined
      // A same-status move is a real event when it carries a reason — it updates
      // the reason text and gets its own history entry for auditing.
      const shouldAppendStatusChange =
        nextStatus !== undefined &&
        (nextStatus !== current.status || Boolean(statusReason?.trim()))

      // Jobs build their status change here instead of going through
      // appendStatusChange, so the reason rule has to be repeated — without it
      // a job could be cancelled silently while a callout could not.
      if (
        shouldAppendStatusChange &&
        nextStatus !== undefined &&
        statusRequiresReason(nextStatus) &&
        !statusReason?.trim()
      ) {
        throw new Error(
          `Reason is required when moving a service to ${nextStatus}`,
        )
      }

      const transaction = adapter.jobsCollection.update(id, (draft) => {
        Object.assign(draft, patch)
        draft.updatedAt = clock.now()
        const draftPatch = draft as Partial<Job> & {
          statusReason?: string
          statusOccurredAt?: string
        }
        delete draftPatch.statusReason
        delete draftPatch.statusOccurredAt
      })

      const statusChangeTransaction = shouldAppendStatusChange && nextStatus
        ? adapter.jobStatusChangesCollection.insert({
            id: idGenerator.generateId(),
            serviceId: id,
            authorId: authStore.user?.id ?? current.authorId ?? '',
            previousStatus: current.status,
            newStatus: nextStatus,
            createdAt: clock.now(),
            occurredAt: statusOccurredAt,
            reason: statusReason,
          } satisfies StatusChangeLog)
        : undefined

      return { transaction, statusChangeTransaction }
    },
    assignJob(jobId: string, userId: string) {
      return assignUserToService<JobAssignment>({
        collection: adapter.jobAssignmentsCollection,
        isForService: (assignment) => assignment.jobId === jobId,
        buildAssignment: ({ id, authorId, now }) => ({
          id,
          jobId,
          userId,
          authorId,
          assignedAt: now,
          unassignedAt: undefined,
          createdAt: now,
          updatedAt: now,
        }),
        userId,
      })
    },
    async assignJobAndWait(jobId: string, userId: string) {
      await awaitAssignResult(this.assignJob(jobId, userId))
    },
    assignCallout(calloutId: string, userId: string) {
      return assignServiceAssignment(adapter.calloutAssignmentsCollection, calloutId, userId)
    },
    async assignCalloutAndWait(calloutId: string, userId: string) {
      await awaitAssignResult(this.assignCallout(calloutId, userId))
    },
    assignSession(sessionId: string, userId: string) {
      return assignServiceAssignment(adapter.sessionAssignmentsCollection, sessionId, userId)
    },
    async assignSessionAndWait(sessionId: string, userId: string) {
      await awaitAssignResult(this.assignSession(sessionId, userId))
    },
    updateSession(id: string, patch: UpdateCalloutInput) {
      updateSessionMutation(id, patch)
    },
    async updateSessionAndWait(id: string, patch: UpdateCalloutInput) {
      const transaction = updateSessionMutation(id, patch)
      if (!transaction) return
      await transaction.isPersisted.promise
      await autoAssignOnWorkStartIfUnassigned({
        nextStatus: patch.status,
        collection: adapter.sessionAssignmentsCollection,
        isForService: (assignment) =>
          (assignment as ServiceAssignment).serviceId === id,
        assign: (userId) => this.assignSession(id, userId),
      })
    },
    async updateJobAndWait(
      id: string,
      patch: Partial<Job> & { statusReason?: string; statusOccurredAt?: string },
    ) {
      const result = this.updateJob(id, patch)
      if (!result) return
      await result.transaction.isPersisted.promise
      // The customer's collection reminder is created and sent by the server
      // (customer_reminders), triggered by the job record itself.
      if (result.statusChangeTransaction) {
        await result.statusChangeTransaction.isPersisted.promise
      }
      await autoAssignOnWorkStartIfUnassigned({
        nextStatus: patch.status,
        collection: adapter.jobAssignmentsCollection,
        isForService: (assignment) =>
          (assignment as JobAssignment).jobId === id,
        assign: (userId) => this.assignJob(id, userId),
      })
    },
    async assignJobNumberTagAndStartAndWait(jobId: string, numberTag: string) {
      const normalizedTag = numberTag.trim()
      if (!normalizedTag) {
        throw new Error('Number tag is required')
      }

      // The receptionist usually assigns the tag from the kiosk-control screen,
      // a different device than the tablet that created the job, so the job may
      // not be in this client's collection yet. Load it through the adapter
      // first so the write still flows through the port/adapter gateway — the
      // optimistic update otherwise silently no-ops when the job is absent,
      // dropping both the tag and the status change.
      await adapter.hydrateRecord('job', jobId)
      if (!adapter.jobsCollection.get?.(jobId)) {
        throw new Error(`Job ${jobId} not found`)
      }

      await this.updateJobAndWait(jobId, {
        numberTag: normalizedTag,
        status: 'ReadyToWork',
      })

      return adapter.jobsCollection.get?.(jobId)
    },
    async completeKioskCheckoutAndWait(input: KioskCheckoutInput) {
      // The tablet running checkout may not have this job loaded yet; pull it
      // through the adapter, then write through the collection port so the
      // PocketBase call happens in the adapter's onUpdate (the gateway).
      await adapter.hydrateRecord('job', input.jobId)
      const currentJob = adapter.jobsCollection.get?.(input.jobId)
      if (!currentJob) {
        throw new Error(`Unable to load job ${input.jobId} for kiosk check-out`)
      }

      const authorId =
        normaliseOptional(input.processedBy) ??
        getCurrentAuthUserId(
          authStore.user?.id,
          (deps.pocketBase as PocketBaseClientLike | undefined)?.authStore,
        ) ?? currentJob.authorId ?? ''
      const noSignatureReason = normaliseOptional(input.noSignatureReason)
      const signedAt = new Date().toISOString()

      const updateTransaction = adapter.jobsCollection.update(input.jobId, (draft) => {
        draft.status = 'ReadyToInvoice'
        draft.checkedOutBy = authorId
        draft.checkedOutName = normaliseOptional(input.checkedOutName)
        draft.checkedOutPhone = normaliseOptional(input.checkedOutPhone)
        draft.checkedOutNoSignatureReason = noSignatureReason ?? ''
        draft.checkedOutSignedAt = signedAt
        draft.collectedAt = signedAt
        if (!noSignatureReason && input.signatureFile) {
          // File rides through the adapter serializer; see buildKioskJob note.
          draft.checkedOutSignature = input.signatureFile as unknown as string
        }
        draft.updatedAt = clock.now()
      })
      await updateTransaction.isPersisted.promise

      if (currentJob.status !== 'ReadyToInvoice') {
        const statusChangeTransaction = adapter.jobStatusChangesCollection.insert({
          id: idGenerator.generateId(),
          serviceId: input.jobId,
          authorId,
          previousStatus: currentJob.status,
          newStatus: 'ReadyToInvoice',
          createdAt: clock.now(),
        } satisfies StatusChangeLog)
        await statusChangeTransaction.isPersisted.promise
      }

      // Refresh from the server so the signature filename and canonical fields
      // replace the optimistic values.
      await adapter.hydrateRecord('job', input.jobId)

      const persistedJob = adapter.jobsCollection.get?.(input.jobId)
      if (!persistedJob) {
        throw new Error(`Unable to sync kiosk checkout for job ${input.jobId}`)
      }

      return persistedJob
    },
    async createKioskJobAndWait(input: CreateKioskJobInput) {
      const checkedInBy =
        normaliseOptional(input.processedBy) ??
        getCurrentAuthUserId(
          authStore.user?.id,
          (deps.pocketBase as PocketBaseClientLike | undefined)?.authStore,
        )

      // Insert through the collection port so the PocketBase create happens in
      // the adapter's onInsert (the gateway), then refetch to pick up the
      // server-assigned public_id and signature filename.
      const job = buildKioskJob({
        id: idGenerator.generateId(),
        now: clock.now(),
        customerId: input.customerId,
        formData: input.formData,
        signatureFile: input.signatureFile,
        noSignatureReason: input.noSignatureReason,
        checkedInBy,
      })

      await adapter.jobsCollection.insert(job).isPersisted.promise
      await adapter.hydrateRecord('job', job.id)

      const persistedJob = adapter.jobsCollection.get?.(job.id)
      if (!persistedJob) {
        throw new Error(`Unable to sync kiosk job ${job.id}`)
      }

      return persistedJob
    },
    updateCallout(id: string, patch: UpdateCalloutInput) {
      updateCalloutMutation(id, patch)
    },
    async updateCalloutAndWait(id: string, patch: UpdateCalloutInput) {
      const transaction = updateCalloutMutation(id, patch)
      if (!transaction) return
      await transaction.isPersisted.promise
      await autoAssignOnWorkStartIfUnassigned({
        nextStatus: patch.status,
        collection: adapter.calloutAssignmentsCollection,
        isForService: (assignment) =>
          (assignment as ServiceAssignment).serviceId === id,
        assign: (userId) => this.assignCallout(id, userId),
      })
    },
    appendLog(serviceId: string, input: NewLogInput) {
      return appendLogCommand(serviceId, input, {
        authStore,
        clock,
        idGenerator,
        calloutsRepo: adapter.calloutsCollection,
        notesRepo: adapter.notesCollection,
      })
    },
    appendLogWithTransaction(serviceId: string, input: NewLogInput) {
      return appendLogWithTransactionCommand(serviceId, input, {
        authStore,
        clock,
        idGenerator,
        calloutsRepo: adapter.calloutsCollection,
        notesRepo: adapter.notesCollection,
      })
    },
    appendStatusChange(serviceId: string, input: NewStatusChangeInput): StatusChangeLog {
      return appendStatusChangeCommand(serviceId, input, {
        authStore,
        clock,
        idGenerator,
        calloutsRepo: adapter.calloutsCollection,
        statusChangesRepo: adapter.statusChangesCollection,
      })
    },
    appendSessionLog(sessionId: string, input: NewLogInput) {
      return appendLogCommand(sessionId, input, {
        authStore,
        clock,
        idGenerator,
        calloutsRepo: adapter.sessionsCollection,
        notesRepo: adapter.sessionNotesCollection,
      })
    },
    appendJobLog(jobId: string, input: NewLogInput) {
      return appendLogCommand(jobId, input, {
        authStore,
        clock,
        idGenerator,
        calloutsRepo: adapter.jobsCollection as JobsRepositoryPort,
        notesRepo: adapter.jobNotesCollection,
      })
    },
    appendSessionLogWithTransaction(sessionId: string, input: NewLogInput) {
      return appendLogWithTransactionCommand(sessionId, input, {
        authStore,
        clock,
        idGenerator,
        calloutsRepo: adapter.sessionsCollection,
        notesRepo: adapter.sessionNotesCollection,
      })
    },
    appendJobLogWithTransaction(jobId: string, input: NewLogInput) {
      return appendLogWithTransactionCommand(jobId, input, {
        authStore,
        clock,
        idGenerator,
        calloutsRepo: adapter.jobsCollection as JobsRepositoryPort,
        notesRepo: adapter.jobNotesCollection,
      })
    },
    appendSessionStatusChange(sessionId: string, input: NewStatusChangeInput): StatusChangeLog {
      return appendStatusChangeCommand(sessionId, input, {
        authStore,
        clock,
        idGenerator,
        calloutsRepo: adapter.sessionsCollection,
        statusChangesRepo: adapter.sessionStatusChangesCollection,
      })
    },
    updateJobLog(jobId: string, logId: string, patch: UpdateLogInput) {
      return updateLogCommand(jobId, logId, patch, {
        authStore,
        clock,
        calloutsRepo: adapter.jobsCollection as JobsRepositoryPort,
        notesRepo: adapter.jobNotesCollection,
      })
    },
    deleteJobLog(jobId: string, logId: string) {
      return deleteLogCommand(jobId, logId, {
        authStore,
        clock,
        calloutsRepo: adapter.jobsCollection as JobsRepositoryPort,
        notesRepo: adapter.jobNotesCollection,
      })
    },
    updateLog(serviceId: string, logId: string, patch: UpdateLogInput) {
      return updateLogCommand(serviceId, logId, patch, {
        authStore,
        clock,
        calloutsRepo: adapter.calloutsCollection,
        notesRepo: adapter.notesCollection,
      })
    },
    deleteLog(serviceId: string, logId: string) {
      return deleteLogCommand(serviceId, logId, {
        authStore,
        clock,
        calloutsRepo: adapter.calloutsCollection,
        notesRepo: adapter.notesCollection,
      })
    },
    // Sessions previously had no note update/delete methods, so the session UI
    // mutated the collection directly and skipped the audit trail entirely.
    updateSessionLog(sessionId: string, logId: string, patch: UpdateLogInput) {
      return updateLogCommand(sessionId, logId, patch, {
        authStore,
        clock,
        calloutsRepo: adapter.sessionsCollection,
        notesRepo: adapter.sessionNotesCollection,
      })
    },
    deleteSessionLog(sessionId: string, logId: string) {
      return deleteLogCommand(sessionId, logId, {
        authStore,
        clock,
        calloutsRepo: adapter.sessionsCollection,
        notesRepo: adapter.sessionNotesCollection,
      })
    },
    /** Hard delete for a note whose photo upload failed — see `discardLog`. */
    discardLog(serviceId: string, logId: string) {
      return discardLogCommand(serviceId, logId, {
        clock,
        calloutsRepo: adapter.calloutsCollection,
        notesRepo: adapter.notesCollection,
      })
    },
    discardSessionLog(sessionId: string, logId: string) {
      return discardLogCommand(sessionId, logId, {
        clock,
        calloutsRepo: adapter.sessionsCollection,
        notesRepo: adapter.sessionNotesCollection,
      })
    },
    discardJobLog(jobId: string, logId: string) {
      return discardLogCommand(jobId, logId, {
        clock,
        calloutsRepo: adapter.jobsCollection as JobsRepositoryPort,
        notesRepo: adapter.jobNotesCollection,
      })
    },
    appendCustomerNote(customerId: string, input: NewCustomerNoteInput) {
      return appendCustomerNoteCommand(customerId, input, {
        authStore,
        clock,
        idGenerator,
        customerNotesRepo: adapter.customerNotesCollection,
      })
    },
    appendCustomerNoteWithTransaction(
      customerId: string,
      input: NewCustomerNoteInput,
    ) {
      return appendCustomerNoteWithTransactionCommand(customerId, input, {
        authStore,
        clock,
        idGenerator,
        customerNotesRepo: adapter.customerNotesCollection,
      })
    },
    updateCustomerNote(
      customerId: string,
      noteId: string,
      patch: UpdateCustomerNoteInput,
    ) {
      return updateCustomerNoteCommand(customerId, noteId, patch, {
        authStore,
        clock,
        customerNotesRepo: adapter.customerNotesCollection,
      })
    },
    deleteCustomerNote(customerId: string, noteId: string) {
      return deleteCustomerNoteCommand(customerId, noteId, {
        authStore,
        clock,
        customerNotesRepo: adapter.customerNotesCollection,
      })
    },
    discardCustomerNote(noteId: string) {
      return discardCustomerNoteCommand(noteId, {
        customerNotesRepo: adapter.customerNotesCollection,
      })
    },
    addCustomerDocumentWithTransaction(
      customerId: string,
      input: NewCustomerDocumentInput,
    ) {
      return addCustomerDocumentWithTransactionCommand(customerId, input, {
        authStore,
        clock,
        idGenerator,
        customerDocumentsRepo: adapter.customerDocumentsCollection,
      })
    },
    deleteCustomerDocument(customerId: string, documentId: string) {
      return deleteCustomerDocumentCommand(customerId, documentId, {
        authStore,
        clock,
        customerDocumentsRepo: adapter.customerDocumentsCollection,
      })
    },
    discardCustomerDocument(documentId: string) {
      return discardCustomerDocumentCommand(documentId, {
        customerDocumentsRepo: adapter.customerDocumentsCollection,
      })
    },
    createFollowUp(input: NewFollowUpInput): FollowUp {
      return createFollowUpCommand(input, {
        authStore,
        clock,
        idGenerator,
        followUpsRepo: adapter.followUpsCollection,
      })
    },
    completeFollowUp(followUpId: string, options: { note?: string } = {}) {
      return completeFollowUpCommand(followUpId, options, followUpDeps)
    },
    snoozeFollowUp(
      followUpId: string,
      step: ScheduleStep,
      options: { note?: string } = {},
    ) {
      return snoozeFollowUpCommand(followUpId, step, options, followUpDeps)
    },
    rescheduleFollowUp(
      followUpId: string,
      dueAt: string,
      options: { note?: string } = {},
    ) {
      return rescheduleFollowUpCommand(followUpId, dueAt, options, followUpDeps)
    },
    closeFollowUp(followUpId: string, options: { note?: string } = {}) {
      return closeFollowUpCommand(followUpId, options, followUpDeps)
    },
    assignFollowUp(followUpId: string, assigneeId: string) {
      return assignFollowUpCommand(followUpId, assigneeId, {
        authStore,
        clock,
        followUpsRepo: adapter.followUpsCollection,
      })
    },
    reopenFollowUp(
      followUpId: string,
      dueAt: string,
      options: { note?: string } = {},
    ) {
      return reopenFollowUpCommand(followUpId, dueAt, options, followUpDeps)
    },
    updateFollowUpDetails(
      followUpId: string,
      patch: Parameters<typeof updateFollowUpDetailsCommand>[1],
    ) {
      return updateFollowUpDetailsCommand(followUpId, patch, {
        authStore,
        clock,
        followUpsRepo: adapter.followUpsCollection,
      })
    },
    discardFollowUp(followUpId: string) {
      return discardFollowUpCommand(followUpId, {
        followUpsRepo: adapter.followUpsCollection,
      })
    },
    async appendAddendumAndWait(
      serviceId: string,
      input: NewAddendumInput,
    ): Promise<Addendum> {
      const { addendum, transaction } = appendAddendumWithTransactionCommand(
        serviceId,
        input,
        {
          authStore,
          clock,
          idGenerator,
          calloutsRepo: adapter.calloutsCollection,
          addendumsRepo: adapter.addendumsCollection,
        },
      )
      await transaction.isPersisted.promise
      return addendum
    },
    async appendSessionAddendumAndWait(
      sessionId: string,
      input: NewAddendumInput,
    ): Promise<Addendum> {
      const { addendum, transaction } = appendAddendumWithTransactionCommand(
        sessionId,
        input,
        {
          authStore,
          clock,
          idGenerator,
          calloutsRepo: adapter.sessionsCollection,
          addendumsRepo: adapter.sessionAddendumsCollection,
        },
      )
      await transaction.isPersisted.promise
      return addendum
    },
    async appendJobAddendumAndWait(
      jobId: string,
      input: NewAddendumInput,
    ): Promise<Addendum> {
      const { addendum, transaction } = appendAddendumWithTransactionCommand(
        jobId,
        input,
        {
          authStore,
          clock,
          idGenerator,
          calloutsRepo: adapter.jobsCollection as JobsRepositoryPort,
          addendumsRepo: adapter.jobAddendumsCollection,
        },
      )
      await transaction.isPersisted.promise
      return addendum
    },
  }
  return db
}

export type Db = ReturnType<typeof createDb>

export type {
  Addendum,
  AddendumChange,
  AuthResponse,
  DemoUser,
  Log,
  NewAddendumInput,
  NewCalloutInput,
  NewCustomerInput,
  NewLogInput,
  NewStatusChangeInput,
  NewWhatsAppMessageInput,
  PhotoLog,
  StatusChangeLog,
  TextLog,
  UpdatePasswordInput,
  UpdateProfileInput,
  UpdateCalloutInput,
  UpdateCustomerInput,
  UpdateLogInput,
  User,
  Job,
}

export type { Note } from '@/db/domain/models'
export type { BillableConsumed, AuditLog, CustomerRevision, ServiceRevision, StatusChange } from '@/lib/schemas/db'
